import { UAParser } from 'ua-parser-js';
import { Component, OnDestroy } from '@angular/core';
import { WizardPageComponent } from '../../../../core/wizard/wizard-page-component';
import type { WelcomeWizard } from '../welcome-wizard';
import { Observable } from 'rxjs';
import { MapMode } from '../../../../map/map-mode.enum';
import { MtnMapOptions } from '../../../../map/mtn-map-options';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { buildDefaultMapSnapshot } from '../../../../map/map-snapshot';
import { UserPreferences } from '../../../../core/user-preferences/user-preferences';
import { UserSetUserPreferences } from '../../../user-actions';
import { ToastService } from '../../../../core/toast/toast.service';
import { UserPreferencesService } from '../../../../core/user-preferences/user-preferences.service';
import { AppState } from '../../../../app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import { MtnMap } from '../../../../map/mtn-map';
import { FilterGroup } from '../../../../core/federation/filter/filter-group';
import { HitTrackingService } from '../../../../core/hit-tracking/hit-tracking.service';
import MapTypeId = google.maps.MapTypeId;
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';

@Component({
  standalone: false,
  selector: 'mtn-welcome-wizard-map-page',
  templateUrl: './welcome-wizard-map-page.component.html',
  styleUrls: ['./welcome-wizard-map-page.component.scss']
})
export class WelcomeWizardMapPageComponent extends WizardPageComponent<WelcomeWizard> implements OnDestroy {

  key = 'map';

  MapMode = MapMode;
  MapTypeId = MapTypeId;
  mapOptions: MtnMapOptions;

  private map: MtnMap;

  constructor(
    private ngrxStore: NgrxStore<AppState>,
    private toaster: ToastService,
    private userPreferencesService: UserPreferencesService,
    private hitTrackingService: HitTrackingService,
  ) {
    super();
    this.title = 'Welcome! Let\'s get started.';
    this.stepperText = 'Maps';
    this.backButtonText = 'Back';
    this.closeButtonText = 'Close';
    this.doNotShowAgainCheckbox = 'Don\'t show again';
    this.headerText = 'Map Preferences';
    this.nextButtonText = 'Next';
  }

  onMapReady(map: MtnMap): void {
    this.map = map;
  }

  onLoad(): Observable<any> {
    this.initForm();
    this.initMap();
    return super.onLoad();
  }

  onClose(): Observable<null> {
    const formValue = this.form?.getRawValue?.() || {};
    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();
    if (this.wizard.doNotShowAgainCheckboxChecked) {
      const request = _.cloneDeep(this.wizard.model.userPreferences);
      request.isWelcomeWizardComplete = true;
      this.userPreferencesService.updateOne(request)
        .pipe(map(() => null));
    }
    // 🔹 Track Close
    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_MAP,
      {
        ...meta,
        ...OSmetadata,
        ...formValue
      },
      USER_TRACKING_ACTIONS.CLOSE_MAP,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.CLOSE_MAP],
      false
    ).subscribe();



    return super.onClose();
  }


  onDonotShowAgainClick(): any {
    const parser = new UAParser(navigator.userAgent).getResult();
    let deviceType = parser.device.type;
    if (!deviceType) {
      deviceType = 'desktop';
    }
    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_MAP,
      { ...meta, ...OSmetadata },
      USER_TRACKING_ACTIONS.OPT_OUT_MAPS,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.OPT_OUT_MAPS],
      false
    ).subscribe();
  }

  onDonotShowAgainUnchecked(): any {
    const parser = new UAParser(navigator.userAgent).getResult();
    let deviceType = parser.device.type;
    if (!deviceType) {
      deviceType = 'desktop';
    }
    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_MAP,
      { ...meta, ...OSmetadata },
      USER_TRACKING_ACTIONS.OPT_IN_MAPS,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.OPT_IN_MAPS],
      false
    ).subscribe();
  }


  onNext(): Observable<string> {
    const formValue = this.form.getRawValue();

    const request = this.wizard.model.request;
    request.mapDefaultMode = formValue.mapDefaultMode;
    request.mapDefaultType = formValue.mapDefaultType;
    request.isWelcomeWizardComplete = true;

    const meta = this.buildTrackingMeta();
    const OSmetadata = this.hitTrackingService.getFirstTimeSetupMetadata();

    // 🔹 Track Next (include form values)
    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.FIRST_TIME_MAP,
      {
        ...meta,
        ...OSmetadata,
        ...formValue,
        selectedPreferences: {
          mapDefaultMode: formValue.mapDefaultMode,
          mapDefaultType: formValue.mapDefaultType
        }
      },
      USER_TRACKING_ACTIONS.NEXT,
      USER_TRACKING_ACTION_DESCRIPTIONS.USER_TRACKING_ACTIONS_NEXT_MAPS,
      false
    ).subscribe();

    return this.userPreferencesService.updateOne(request)
      .pipe(map((result: UserPreferences) => {
        this.ngrxStore.dispatch(UserSetUserPreferences({ preferences: result }));
        this.toaster.info('Successfully updated preferences');
        return 'end';
      }));
  }


  ngOnDestroy(): void {
    if (!this.wizard.model.userPreferences?.isWelcomeWizardComplete) {

    }
  }

  private initForm(): void {
    this.form = new UntypedFormGroup({
      mapDefaultMode: new UntypedFormControl(this.wizard.model.userPreferences.mapDefaultMode, [Validators.required]),
      mapDefaultType: new UntypedFormControl(this.wizard.model.userPreferences.mapDefaultType, [Validators.required])
    });

    this.subscribeToFormChanges();
  }

  private initMap(): void {
    const snapshot = buildDefaultMapSnapshot(
      this.wizard.model.userPreferences.mapDefaultMode,
      this.wizard.model.userPreferences.mapDefaultType
    );

    const options = snapshot.options;
    options.key = 'welcome-wizard-preferences-map-2';
    options.filterGroup = new FilterGroup();
    options.isAutoSaveEnabled = false;
    options.isAutoUpdateEnabled = false;
    options.isFeatureMap = false;
    options.controlConfiguration.isGoogleSearchEnabled = false;
    options.controlConfiguration.isFiltersEnabled = false;
    options.controlConfiguration.isMapModeEnabled = false;
    options.controlConfiguration.isMapTypeEnabled = false;
    options.controlConfiguration.isRecenterEnabled = false;
    options.controlConfiguration.isStreetViewEnabled = false;
    options.controlConfiguration.isTiltAndRotateEnabled = false;
    options.controlConfiguration.isZoomEnabled = false;

    this.mapOptions = options;
  }

  private subscribeToFormChanges(): void {
    this.addSubscription(
      this.form.valueChanges.subscribe((value: any) => {
        if (this.map) {
          this.map.setOptions({
            mode: value.mapDefaultMode,
            mapTypeId: value.mapDefaultType
          });
        }
      })
    );
  }

  private buildTrackingMeta(): any {
    return {
      time: new Date().toISOString()
    };
  }
}
